// src/components/ConfettiBurst.jsx
// Lightweight canvas confetti burst, self-contained (no external deps) so
// it stays tiny and themeable in the brand's own palette. Fires once each
// time `fireKey` changes to a new truthy value.

import React, { useEffect, useRef } from 'react';

const COLORS = ['#CEAC8F', '#f5e6d3', '#ffffff', '#8a6d52', '#141414'];
const PARTICLE_COUNT = 90;
const GRAVITY = 0.16;
const DURATION_MS = 2600;

const ConfettiBurst = ({ fireKey }) => {
    const canvasRef = useRef(null);
    const rafRef = useRef(null);

    useEffect(() => {
        if (!fireKey) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx2d = canvas.getContext('2d');

        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx2d.scale(dpr, dpr);

        const cx = rect.width / 2;
        const cy = rect.height / 2;

        const particles = Array.from({ length: PARTICLE_COUNT }, () => {
            const angle = Math.random() * Math.PI * 2;
            const speed = 3 + Math.random() * 6;
            return {
                x: cx,
                y: cy,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed - 2,
                size: 4 + Math.random() * 5,
                color: COLORS[Math.floor(Math.random() * COLORS.length)],
                rotation: Math.random() * 360,
                spin: (Math.random() - 0.5) * 18,
                tilt: Math.random(),
            };
        });

        const start = performance.now();

        const frame = (now) => {
            const elapsed = now - start;
            const life = elapsed / DURATION_MS;
            ctx2d.clearRect(0, 0, rect.width, rect.height);

            if (life >= 1) {
                rafRef.current = null;
                return;
            }

            const fade = 1 - life;
            particles.forEach((p) => {
                p.vy += GRAVITY;
                p.x += p.vx;
                p.y += p.vy;
                p.rotation += p.spin;

                ctx2d.save();
                ctx2d.globalAlpha = fade;
                ctx2d.translate(p.x, p.y);
                ctx2d.rotate((p.rotation * Math.PI) / 180);
                ctx2d.fillStyle = p.color;
                const w = p.size;
                const h = p.size * (0.4 + p.tilt * 0.6);
                ctx2d.fillRect(-w / 2, -h / 2, w, h);
                ctx2d.restore();
            });

            rafRef.current = requestAnimationFrame(frame);
        };

        rafRef.current = requestAnimationFrame(frame);

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [fireKey]);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                inset: '-70%',
                pointerEvents: 'none',
                zIndex: 7,
            }}
        />
    );
};

export default ConfettiBurst;
